import React, {useMemo, useState} from 'react'

import {Button} from '@vio/atlas/Button'
import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {ImagePlaceholder} from '@vio/atlas/helpers/ImagePlaceholder'
import {Icon} from '@vio/atlas/Icon'
import {useDispatchTeamEvent} from '@vio/core/src/analytics/hooks/useDispatchTeamEvent'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {Action, Category, Entity} from '@vio/core/src/analytics/types/Events'
import {sortTopRoomImagesByCaption} from '@vio/core/src/room/business/utils/sortRoomImages'

import {GallerySingleView} from '../GallerySingleView'
import {LightboxGalleryMobile} from '../LightboxGalleryMobile'
import {SwipeGallery} from '../SwipeGallery'

import type {RoomImage} from '@findhotel/sapi'
import type {ImageProvider} from '@vio/core/src/staticContent/types/ImageProvider'

interface Props {
  images: RoomImage[]
  imageProvider: ImageProvider
}
export const RoomDetailsOverlayImage = ({images, imageProvider}: Props) => {
  const {isMobile, isDesktopXs} = useDeviceLayout()
  const isMobileOrTablet = isMobile || isDesktopXs
  const [isLightboxOpen, setIsLightboxOpen] = useState(false)
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const dispatchTeamEvent = useDispatchTeamEvent()

  const imageUrls = useMemo(
    () => sortTopRoomImagesByCaption(images).map(image => image.url),
    [images]
  )

  const handleOpenLightbox = () => {
    setIsLightboxOpen(true)
  }

  const handleSwipeGallery = (index: number) => {
    setCurrentImageIndex(index)
  }

  const handleSwipe = () => {
    if (isLightboxOpen) {
      dispatchTeamEvent(
        trackEvent({
          category: Category.User,
          entity: Entity.FullScreenRoomImageGallery,
          action: Action.Scrolled
        })
      )
    }
  }

  return isMobileOrTablet ? (
    <>
      <SwipeGallery
        images={imageUrls}
        imageProvider={imageProvider}
        backgroundSize="cover"
        isRtl={false}
        onImageClick={handleOpenLightbox}
        onSwipe={handleSwipeGallery}
        showPageNumber
        hasDarkBackground
        placeHolder={<ImagePlaceholder type="room" />}
      />
      <LightboxGalleryMobile
        isOpen={isLightboxOpen}
        images={imageUrls}
        imageProvider={imageProvider}
        initialImageIndex={currentImageIndex}
        isRtl={false}
        onClose={() => setIsLightboxOpen(false)}
        onSwipe={handleSwipe}
        rightHeaderButtonSlot={
          <Button
            variant="tertiary"
            isRounded
            isFloating
            dataId="BackButton"
            iconStart={<Icon name="Close" />}
            onClick={() => setIsLightboxOpen(false)}
            size="lg"
          />
        }
        hasLeftHeaderButtonSlot={false}
      />
    </>
  ) : (
    <GallerySingleView
      imageProvider={imageProvider}
      images={imageUrls}
      blurBackground
      showImageDetails={false}
      component={Entity.RoomImageGallery}
    />
  )
}
